import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Stack,
    TextField,
    LinearProgress,
    Divider, useTheme
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveChangesButtonGroup from '../../../modules/components/titledPaper/titledPaperWithSaveBar/saveBar';
import { Formik, Form, Field } from 'formik';
import { Suspense } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Variable } from '../../../model/interfaces/variable';
import { TypographyField } from '../../../modules/formikFields/typographyField';
import { XmlField } from '../../../modules/formikFields/xmlField';
import { VariableTestResult } from './variableTestResult';
import { useActionModal } from '../../../modules/actionModal';
import { useInvalidator } from 'rest-hooks';
import { VariableResultPreview } from '../../../model/entities/variableResultPreview';
import { useApplication } from '../../../model/context/applicationContext';

interface VariableListItemProps {
    isOpen: boolean;
    variable: Variable;
    saveVariable: (variable: Variable) => void;
    deleteVariable: (variable: Variable) => void;
    onToggle: (accordionId: string, isOpen: boolean) => void;
}

/**
 *
 * @param props Variables and functions needed for a single Accordion
 * @returns The rendered accordion
 */
export const VariableListItem = (props: VariableListItemProps) => {
    const { isOpen, variable, saveVariable, deleteVariable, onToggle } = props;

    const { openActionModal } = useActionModal();
    const intl = useIntl();
    const theme = useTheme();

    const handleChange = () => {
        onToggle(variable.variableId.toString(), isOpen);
    };

    const invalidator = useInvalidator(VariableResultPreview);

    const requestDeleteVariable = (variable: Variable) => {
        openActionModal(
            <FormattedMessage
                id="deleteTitle"
                defaultMessage="Delete {variable}?"
                values={{ variable: variable.name }}
            />,
            <FormattedMessage
                id="delete"
                defaultMessage={'Are you sure you want to delete {variable}?'}
                values={{
                    variable: variable.name
                }}
            />,
            'Delete',
            'Cancel',
            () => deleteVariable(variable)
        );
    };

    const { applicationNumber, applicationXml } = useApplication();

    return (
        <Formik
            initialValues={variable}
            onSubmit={(variable, { setSubmitting }) => {
                saveVariable(variable);
                if (variable.variableId && applicationNumber && applicationXml) {
                    invalidator({
                        variableId: variable.variableId,
                        applicationNumber: applicationNumber,
                        applicationXml: applicationXml
                    });
                }
                setSubmitting(false);
            }}
            enableReinitialize>
            <Form>
                <Accordion
                    expanded={isOpen}
                    onChange={handleChange}
                    disableGutters
                    elevation={0}
                    sx={{
                        backgroundColor: '#fff',
                        border: '1px solid #E3E2DE',
                        borderRadius: '6px !important',
                        '&:before': { display: 'none' },
                        '&:hover, &.Mui-expanded': { borderColor: '#3B82F6' }
                    }}
                    TransitionProps={{ unmountOnExit: true }}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                            backgroundColor: '#fff',
                            borderRadius: '6px',
                            minHeight: 0,
                            paddingY: 0.5,
                            '.MuiAccordionSummary-content': { minWidth: '0', flexDirection: 'column', margin: '6px 0' }
                        }}>
                        <TypographyField
                            name={'name'}
                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                            noWrap
                            variant={'body2'}
                        />
                        <TypographyField
                            name={'query'}
                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                            noWrap
                            variant="caption"
                            color={'text.secondary'}
                        />
                    </AccordionSummary>

                    <AccordionDetails sx={{ backgroundColor: '#fff' }}>
                        <Stack spacing={1}>
                            <Field
                                label={intl.formatMessage({
                                    id: 'VariableNameLabel'
                                })}
                                name={'name'}
                                color={'primary'}
                                as={TextField}
                                inputProps={{
                                    style: { ...theme.typography.body2 }
                                }}
                            />
                            <XmlField name={'query'} variant="variable" />
                            <Suspense fallback={<LinearProgress />}>
                                <VariableTestResult variable={variable} isOpen={isOpen} />
                            </Suspense>
                            <Divider />
                            <SaveChangesButtonGroup
                                onClickDelete={() => requestDeleteVariable(variable)}
                                changeDate={variable.changedDate}
                            />
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            </Form>
        </Formik>
    );
};
